/**
 * Intercepteur pour les erreurs Firestore
 * Gère automatiquement les erreurs de connexion et d'authentification
 */

import { auth } from '../services/firebase/config';

// Fonction utilitaire pour détecter les erreurs de connexion
const isConnectionError = (error) => {
  return error.code === 'unavailable' ||
         error.code === 'failed-precondition' ||
         error.message?.includes('Failed to get document because the client is offline') ||
         error.message?.includes('WebChannelConnection') ||
         error.message?.includes('transport errored');
};

// Fonction utilitaire pour détecter les erreurs d'authentification
const isAuthError = (error) => {
  return error.code === 'permission-denied' ||
         error.code === 'unauthenticated' ||
         error.message?.includes('permission-denied') ||
         error.message?.includes('unauthenticated');
};

/**
 * Intercepte et gère les erreurs Firestore
 * @param {Error} error - L'erreur à traiter
 * @param {string} operation - Description de l'opération qui a échoué
 * @param {Function} retryFn - Fonction à appeler pour réessayer l'opération
 * @returns {Promise<any>} - Résultat de la réessai ou lève l'erreur originale
 */
export const handleFirestoreError = async (error, operation = 'opération Firestore', retryFn = null) => {

  // Gestion spécifique des erreurs de connexion
  if (isConnectionError(error)) {
    
    // Attendre un peu avant de réessayer
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Si une fonction de réessai est fournie, l'utiliser
    if (typeof retryFn === 'function') {
      try {
        return await retryFn();
      } catch (retryError) {

        // Si le réessai échoue aussi avec une erreur de connexion, 
        // permettre à l'application de continuer en mode dégradé
        if (isConnectionError(retryError)) {

          return null; // ou une valeur par défaut appropriée
        }
        
        throw retryError;
      }
    }
    
    // Si pas de fonction de réessai, laisser l'application gérer l'erreur

    return null;
  }

  // Gestion spécifique des erreurs d'authentification
  if (isAuthError(error)) {
    
    // Tenter de récupérer le contexte d'authentification
    if (typeof window !== 'undefined' && window.__authContext) {
      try {
        const refreshFn = window.__authContext.refreshFirebaseToken;
        if (typeof refreshFn === 'function') {
          const refreshed = await refreshFn();
          
          if (refreshed) {
            
            // Réessayer l'opération si une fonction de réessai est fournie
            if (typeof retryFn === 'function') {
              return await retryFn();
            }
          } else {

            // Marquer comme nécessitant une reconnexion
            localStorage.setItem('auth_refresh_failed', 'true');
          }
        } else {

        }
      } catch (refreshError) {

      }
    } else {

      // Flag pour indiquer un besoin de reconnexion
      localStorage.setItem('auth_refresh_needed', 'true');
    }
  }

  // Si aucune gestion spécifique n'a pu résoudre le problème, relancer l'erreur
  throw error;
};

/**
 * Wrapper pour les opérations Firestore avec gestion automatique des erreurs
 * @param {Function} operation - Fonction à exécuter
 * @param {string} operationName - Nom de l'opération pour les logs
 * @returns {Promise<any>} - Résultat de l'opération
 */
export const withFirestoreErrorHandling = async (operation, operationName = 'opération') => {
  try {
    return await operation();
  } catch (error) {
    return await handleFirestoreError(error, operationName, operation);
  }
};

/**
 * Vérifie si l'application est en mode hors ligne
 * @returns {boolean} - true si hors ligne
 */
export const isOfflineMode = () => {
  if (typeof window === 'undefined') return false;
  
  // Vérifier le statut réseau général
  if (!navigator.onLine) {
    return true;
  }
  
  // Vérifier le statut Firestore si disponible
  if (typeof window.__firestoreConnectionStatus === 'function') {
    return !window.__firestoreConnectionStatus();
  }
  
  return false;
};

/**
 * Attend la reconnexion réseau
 * @param {number} maxWaitTime - Temps d'attente maximum en millisecondes
 * @returns {Promise<boolean>} - true si reconnecté, false si timeout
 */
export const waitForReconnection = (maxWaitTime = 30000) => {
  return new Promise((resolve) => {
    const startTime = Date.now();
    
    const checkConnection = () => {
      if (!isOfflineMode()) {
        resolve(true);
        return;
      }
      
      if (Date.now() - startTime >= maxWaitTime) {
        resolve(false);
        return;
      }
      
      setTimeout(checkConnection, 1000);
    };
    
    checkConnection();
  });
}; 